import { adaptSearchRequestBody } from '~/utils/meiliRequestAdapter'
import { adaptSearchResponseBody } from '~/utils/meiliResponseAdapter'

export type MeiliSearchHttpClientOptions = {
    /** Origin of the A4G Search API, e.g. `https://dev-search-a4g.dmf-kunden.com` (no path). */
    baseUrl: string
    /** Public, search-scoped bearer key. */
    apiKey: string
}

const API_BASE_PATH = '/api/search/v1'

type MultiSearchQuery = { indexUid?: string, q?: string }

/**
 * Custom `httpClient` for `@meilisearch/instant-meilisearch` (ADR-0008) — the single
 * seam carrying the A4G Search API's base path (`/api/search/v1`) and the public,
 * search-scoped bearer key. Every request/response also runs through the pure
 * `meiliRequestAdapter`/`meiliResponseAdapter` functions.
 *
 * Ignores whatever host the adapter itself resolved the request against (it is only
 * ever passed a placeholder — see `useMeiliSearch`) and always targets `baseUrl`.
 *
 * A `multi-search` in which no query carries a term is answered here instead of being
 * sent (see `emptyMultiSearchResponse`).
 */
export function createMeiliSearchHttpClient({ baseUrl, apiKey }: MeiliSearchHttpClientOptions) {
    return async function meiliSearchHttpClient(input: RequestInfo | URL, init: RequestInit = {}): Promise<unknown> {
        const url = new URL(`${API_BASE_PATH}${resolveRequestPath(input)}`, baseUrl)

        const headers = new Headers(init.headers)
        headers.set('Authorization', `Bearer ${apiKey}`)

        const adaptedBody = typeof init.body === 'string'
            ? adaptSearchRequestBody(JSON.parse(init.body))
            : init.body

        const queries = queriesWithoutTerm(adaptedBody)
        if (queries) {
            return emptyMultiSearchResponse(queries)
        }

        const body = typeof init.body === 'string' ? JSON.stringify(adaptedBody) : adaptedBody as BodyInit

        const response = await fetch(url, { ...init, headers, body })

        if (!response.ok) {
            throw new Error(`A4G Search API request failed with status ${response.status}`)
        }

        return adaptSearchResponseBody(await response.json())
    }
}

/**
 * The queries of a `multi-search` in which not one of them carries a term, else `null`.
 *
 * Two callers produce such a request, neither of which needs the backend:
 * - every `<ais-instant-search>` searches once on mount, before the user typed anything —
 *   the header autocomplete and the search drawer do that on every page view;
 * - `@meilisearch/instant-meilisearch` prefetches an unfiltered facet distribution per
 *   index on its first search (its `init-facets-distribution`). That cache only feeds its
 *   `keepZeroFacets` mode, which is off here, so the response is discarded — lift this
 *   short-circuit if that option is ever turned on in `useMeiliSearch`.
 */
function queriesWithoutTerm(body: unknown): MultiSearchQuery[] | null {
    const queries = (body as { queries?: unknown })?.queries

    if (!Array.isArray(queries) || queries.length === 0) {
        return null
    }

    const hasTerm = queries.some((query: MultiSearchQuery) => (query?.q ?? '').trim() !== '')

    return hasTerm ? null : queries as MultiSearchQuery[]
}

function emptyMultiSearchResponse(queries: MultiSearchQuery[]) {
    return {
        results: queries.map(query => ({
            indexUid: query.indexUid ?? '',
            hits: [],
            query: '',
            processingTimeMs: 0,
            limit: 0,
            offset: 0,
            estimatedTotalHits: 0,
            facetDistribution: {},
            facetStats: {},
        })),
    }
}

function resolveRequestPath(input: RequestInfo | URL): string {
    if (input instanceof URL) {
        return `${input.pathname}${input.search}`
    }

    if (typeof input === 'string') {
        try {
            const parsed = new URL(input)
            return `${parsed.pathname}${parsed.search}`
        } catch {
            return input.startsWith('/') ? input : `/${input}`
        }
    }

    return resolveRequestPath(new URL(input.url))
}
